import { useQuery, keepPreviousData } from "@tanstack/react-query";

export interface StatementSummaryRow {
  statementDate: string;
  invoiceCount: number;
  totalCommission: string;
  totalFare: string;
  grossCommission?: string;
  agentCommission?: string;
  adjustments?: string;
  netPayout?: string;
  source?: "aivia" | "trams";
  /** Live week that is not yet in the statement log. */
  inProgress?: boolean;
}

export interface StatementListResponse {
  items: StatementSummaryRow[];
  totalCount: number;
  viewMode: "my" | "all";
}

export interface StatementListParams {
  page: number;
  pageSize: number;
  sortDir: "asc" | "desc";
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  year?: number;
  month?: number;
}

export interface StatementInvoiceRow {
  id: number;
  aiviaInvoiceId?: number | null;
  aiviaActionsEnabled?: boolean;
  rowKey?: string;
  source?: "aivia" | "trams";
  invoiceNumber: string | null;
  /** TRAMS INVOICE.INVOICENUMBER (metadata.tramsInvoiceNumber). */
  tramsInvoiceNumber: string | null;
  tripId: number;
  primaryTraveler: string | null;
  supplierName: string | null;
  travelType: string | null;
  confirmationNumber: string | null;
  departureDate: string | null;
  returnDate: string | null;
  totalFare: string | null;
  commissionAmount: string | null;
  paidAt: string | null;
  statementDate: string | null;
  /** Whose statement the line sits on. */
  advisorName: string | null;
  /**
   * The advisor who sold the booking, which on an agency owner's statement is
   * usually an associate rather than the owner. Null on rows written before
   * attribution existed, or where TRAMS shows no single seller.
   */
  sellingAgentName?: string | null;
  recordLocator: string | null;
  issueDate: string | null;
  invoiceStatusCode: string | null;
  /** Resolved agent commission rate (%), per travel type + TRAMS profile. */
  agentRatePercent: string | number | null;
  /** Total commission × agent rate (%). */
  agentCommissionAmount: string | null;
  tramsInvoiceNo?: number | null;
  tramsBookingNo?: number | null;
  tramsAgentBkgNo?: number | null;
  agentProfileNo?: number | null;
  reportDate?: string | null;
  section?: string | null;
}

export interface StatementDetailResponse {
  statementDate: string;
  invoiceCount: number;
  totalCommission: string;
  totalFare: string;
  grossCommission?: string;
  agentCommission?: string;
  adjustmentTotal?: string;
  netPayout?: string;
  invoices: StatementInvoiceRow[];
  fromTrams?: boolean;
  inProgress?: boolean;
}

export function useStatementList(params: StatementListParams) {
  const qs = new URLSearchParams();
  qs.set("page", String(params.page));
  qs.set("pageSize", String(params.pageSize));
  qs.set("sortDir", params.sortDir);
  if (params.tramsAccountId)
    qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.year) qs.set("year", String(params.year));
  if (params.month) qs.set("month", String(params.month));

  return useQuery<StatementListResponse>({
    queryKey: ["/api/statements", params],
    queryFn: async () => {
      const res = await fetch(`/api/statements?${qs.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch statements");
      }
      return res.json();
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Distinct years that have statements for the current scope — drives the year filter options. */
export function useStatementYears(params: {
  tramsAccountId?: number;
  viewMode?: "my" | "all";
}) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId)
    qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);

  return useQuery<{ years: number[] }>({
    queryKey: ["/api/statements/years", params],
    queryFn: async () => {
      const res = await fetch(`/api/statements/years?${qs.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch statement years");
      }
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useStatementDetail(
  statementDate: string | undefined,
  params: { tramsAccountId?: number; viewMode?: "my" | "all" },
) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId)
    qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);

  return useQuery<StatementDetailResponse>({
    queryKey: ["/api/statements", statementDate, params],
    queryFn: async () => {
      const res = await fetch(
        `/api/statements/${statementDate}?${qs.toString()}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch statement detail");
      }
      return res.json();
    },
    enabled: !!statementDate,
    staleTime: 30_000,
  });
}
